import React, { FC, useState } from 'react';
import { View } from 'react-native';
import { useTranslation } from '@oolio-group/localization';
import { analyticsService } from '../../analytics/AnalyticsService';
import { useSession } from '../../hooks/useSession';
import { useSettings } from '../../hooks/useSettings';
import { useKitchenDisplay } from '../../hooks/useKitchenDisplay';
import {
  DefaultBumpMessage,
  DefaultReceiveMessage,
} from '../../common/constants';
import SettingsItem from '../../component/SettingsItem/SettingsItem';
import OrderMessageModal, {
  MessageType,
} from '../../component/Modals/OrderMessage/OrderMessageModal';
import KitchenTimerSettings from './KitchenTimerSettings';
import SectionHeading from './SectionHeading';
import SettingsScreenStyles from './Settings.styles';

const TimersAndAlertsSettings: FC = () => {
  const styles = SettingsScreenStyles();
  const { translate } = useTranslation();
  const { session, setSession } = useSession();
  const { audioToggle } = useSettings();
  const { updateKitchenDisplay } = useKitchenDisplay({
    storeId: session?.currentStore?.id,
  });

  const [messageModalVisible, setMessageModalVisible] = useState(false);
  const [messageType, setMessageType] = useState<MessageType>(MessageType.BUMP);

  const {
    notifyOnBump,
    notifyOnReceive,
    bumpMessage: savedBumpMessage,
    receiveMessage: savedReceiveMessage,
  } = session?.kitchenDisplay?.notificationConfig || {};

  const notificationConfig = {
    notifyOnBump: notifyOnBump || false,
    notifyOnReceive: notifyOnReceive || false,
    bumpMessage: savedBumpMessage || DefaultBumpMessage,
    receiveMessage: savedReceiveMessage || DefaultReceiveMessage,
  };

  const closeMessageModal = () => {
    setMessageModalVisible(false);
  };

  const openMessageModal = (type: MessageType) => {
    setMessageType(type);
    setMessageModalVisible(true);
  };

  const onSaveMessage = (pref: MessageType, message: string) => {
    if (pref === MessageType.BUMP) {
      updateKitchenDisplay({
        id: session?.kitchenDisplay?.id || '',
        notificationConfig: {
          ...notificationConfig,
          bumpMessage: message,
        },
      });
      setSession({
        kitchenDisplay: {
          ...session.kitchenDisplay,
          notificationConfig: {
            ...notificationConfig,
            bumpMessage: message,
          },
        },
      });
    } else {
      updateKitchenDisplay({
        id: session?.kitchenDisplay?.id || '',
        notificationConfig: {
          ...notificationConfig,
          receiveMessage: message,
        },
      });
      setSession({
        kitchenDisplay: {
          ...session.kitchenDisplay,
          notificationConfig: {
            ...notificationConfig,
            receiveMessage: message,
          },
        },
      });
    }
    analyticsService.capture('sms_message', {
      type: pref,
      message: message,
    });
    setMessageModalVisible(false);
  };

  const onChangePreference = (pref: MessageType, value: boolean) => {
    switch (pref) {
      case MessageType.BUMP:
        updateKitchenDisplay({
          id: session?.kitchenDisplay?.id || '',
          notificationConfig: {
            ...notificationConfig,
            notifyOnBump: value,
          },
        });
        setSession({
          kitchenDisplay: {
            ...session.kitchenDisplay,
            notificationConfig: {
              ...notificationConfig,
              notifyOnBump: value,
            },
          },
        });
        break;
      case MessageType.RECEIVE:
        updateKitchenDisplay({
          id: session?.kitchenDisplay?.id || '',
          notificationConfig: {
            ...notificationConfig,
            notifyOnReceive: value,
          },
        });
        setSession({
          kitchenDisplay: {
            ...session.kitchenDisplay,
            notificationConfig: {
              ...notificationConfig,
              notifyOnReceive: value,
            },
          },
        });
        break;

      default:
        break;
    }
    analyticsService.capture('enabled_sms_message', {
      type: pref,
      enabled: value,
    });
  };

  return (
    <View style={styles.panel} ph-label="settings-timers-alerts">
      <KitchenTimerSettings />
      <View style={styles.panelSection}>
        <SectionHeading
          testID="heading-alerts"
          title={translate('kitchenDisplay.alerts')}
        />
        <SettingsItem
          type="switch"
          testID="toggle-audio"
          title={translate('kitchenDisplay.audioToggle')}
          checked={session.audioToggle || false}
          onChange={audioToggle}
        />
      </View>
      <View style={styles.panelSection}>
        <SectionHeading
          testID="heading-smsMessaging"
          title={translate('kitchenDisplay.smsMessaging')}
        />
        <SettingsItem
          type="switch"
          testID="order-received-switch"
          title={translate('kitchenDisplay.orderReceived')}
          checked={notifyOnReceive}
          onChange={() =>
            onChangePreference(MessageType.RECEIVE, !notifyOnReceive)
          }
        />
        <View style={styles.subSectionTight}>
          <SettingsItem
            type="action"
            testID={'message-on-sms-received'}
            title={translate('kitchenDisplay.message')}
            subtitle={notificationConfig.receiveMessage}
            onChange={() => openMessageModal(MessageType.RECEIVE)}
          />
        </View>
        <SettingsItem
          type="switch"
          testID="order-bump-switch"
          title={translate('kitchenDisplay.orderBumped')}
          checked={notifyOnBump}
          onChange={() => onChangePreference(MessageType.BUMP, !notifyOnBump)}
        />
        <View style={styles.subSection}>
          <SettingsItem
            type="action"
            testID={'message-on-sms-bump'}
            title={translate('kitchenDisplay.message')}
            subtitle={notificationConfig.bumpMessage}
            onChange={() => openMessageModal(MessageType.BUMP)}
          />
        </View>
      </View>
      <OrderMessageModal
        key={`message${messageModalVisible}`}
        visible={messageModalVisible}
        messageType={messageType}
        onClose={closeMessageModal}
        onSave={onSaveMessage}
        initialMessage={
          messageType === MessageType.BUMP
            ? notificationConfig.bumpMessage
            : notificationConfig.receiveMessage
        }
      />
    </View>
  );
};

export default TimersAndAlertsSettings;
